import type { BillData } from '../schema/billData';
import type { UserProfile } from '../schema/userProfile';

/**
 * The clipboard-handoff payload for the autofill bookmarklet (see
 * src/bookmarklet/ and docs/automation-architecture.md). This never touches
 * any server — it's copied to the OS clipboard by the app and read directly
 * by the bookmarklet running on prize.ird.gov.np. Deliberately excludes the
 * bill photo (attached manually) and anything not needed to fill the real
 * form's text/date/payment-method fields.
 *
 * `v` is a format version: the bookmarklet is a static, cached script (once
 * saved to a user's bookmarks, we can't force-update it), so if this shape
 * ever changes, the bookmarklet can detect a mismatched `v` and show a clear
 * "please reinstall the bookmarklet" message instead of failing silently.
 */
export const AUTOFILL_PAYLOAD_VERSION = 1;

export interface AutofillPayload {
  v: typeof AUTOFILL_PAYLOAD_VERSION;
  billNumber: string;
  sellerPan: string;
  billDateAd: string;
  billDateBs: string;
  amount: string;
  name: string;
  mobileNumber: string;
  address: string;
}

/** Returns null if any field the real form requires is missing — mirrors the same fields Review's confirm gate already validated. */
export function buildAutofillPayload(bill: BillData, profile: UserProfile | null): AutofillPayload | null {
  const { billNumber, sellerPan, billDateAd, billDateBs, amount } = bill;
  if (
    !billNumber.value ||
    !sellerPan.value ||
    !billDateAd.value ||
    !billDateBs.value ||
    amount.value === null ||
    !profile
  ) {
    return null;
  }

  return {
    v: AUTOFILL_PAYLOAD_VERSION,
    billNumber: billNumber.value,
    sellerPan: sellerPan.value,
    billDateAd: billDateAd.value,
    billDateBs: billDateBs.value,
    amount: amount.value.toFixed(2),
    name: profile.name,
    mobileNumber: profile.mobileNumber,
    address: profile.address,
  };
}
